import { debounceTime } from 'rxjs/operators';
import {
    Component,
    Input,
    forwardRef,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Yii } from "libraries/interfaces/Yii";
import { Subject } from 'rxjs';
import { faTimes, IconDefinition } from "@fortawesome/free-solid-svg-icons";

export interface IAutocompleteItem {
    label: string;
    value: string;
}

@Component({
    selector: 'chips-input',
    template: `
        <mat-form-field>
            <mat-chip-list #chipList>
                <mat-chip *ngFor="let item of items; let i = index;"
                          [selectable]="false"
                          [removable]="true"
                          (removed)="remove(i)">
                    {{item.label}}
                    <fa-icon matChipRemove [icon]="times" [title]="labels.remove"></fa-icon>
                </mat-chip>

                <notification
                        *ngIf="disableFreeText && !autocompleteList.length; else inputArea">{{labels.allOptionsUsed}}</notification>
                <ng-template #inputArea>
                    <input #input
                           [afiChipInputFor]="chipList"
                           [name]="name"
                           [placeholder]="placeholder"
                           [matAutocomplete]="auto"
                           [matChipInputAddOnBlur]="true"
                           (matChipInputTokenEnd)="possibleAdd.next($event.value)"
                           (onDelete)="onDelete()"
                           (blur)="onBlur()"
                           (input)="inputChanged($event)"
                           type="text" class="e2e-enter-code-booking">
                </ng-template>

                <mat-autocomplete #auto="matAutocomplete" (optionSelected)="possibleAdd.next($event.option.value)">
                    <mat-option *ngFor="let item of autocompleteList" [value]="item.value">
                        {{item.label}}
                    </mat-option>
                </mat-autocomplete>
            </mat-chip-list>
        </mat-form-field>
    `,
    styleUrls: [
        '../../../../../styles/angular-material/chip-list.scss',
        '../../../../../styles/angular-material/form-field.scss',
    ],
    styles: [`
        mat-form-field {
            width: 100%;
        }

        mat-form-field mat-chip-list input {
            border: 1px solid #e6eaed;
        }

        notification {
            height: 32px;
            line-height: 6px;
            flex: 1;
        }
    `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsInputComponent),
            multi: true,
        }
    ]
})
export class ChipsInputComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

    public get value(): string[] {
        return this.values;
    }

    public possibleAdd: Subject<string> = new Subject();
    public times: IconDefinition = faTimes;

    public items: IAutocompleteItem[] = [];
    public labels = {
        remove: Yii.t('wui-shared-js', "Supprimer"),
        allOptionsUsed: Yii.t('wui-shared-js', "Toutes les options sont sélectionnées"),
    };

    @Input()
    public autocompleteList: IAutocompleteItem[] = null;

    @Input()
    public disabled: boolean = false;

    @Input()
    public disableFreeText: boolean = false;

    @Input()
    public toUpper: boolean = false;

    @Input()
    public placeholder: string = "";

    @Input()
    public name: string;

    @Output()
    public readonly onChangeInput: EventEmitter<string> = new EventEmitter();

    @Output()
    public readonly onAdd: EventEmitter<string[]> = new EventEmitter();

    @ViewChild('input')
    public input: ElementRef;
    private values: string[];

    /////////// LIFECYCLE METHODS

    public ngOnInit(): void {
        this.possibleAdd.asObservable()
            .pipe(debounceTime(10))
            .subscribe((value: string) => {
                this.add(value);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.autocompleteList.currentValue !== changes.autocompleteList.previousValue) {
            this.generateItems();
        }
    }

    public ngOnDestroy(): void {
        this.possibleAdd.complete();
    }

    /////////// INSTANCE METHODS

    /// Interface ControlValueAccessor
    /// Needed to use this control as a reactive form control
    public writeValue(values: string[]): void {
        this.values = values || [];
        this.generateItems();
        this.onChange(this.values);
    }

    // Allows Angular to register a function to call when the model (rating) changes.
    // Save the function as a property to call later here.
    public registerOnChange(fn: (values: string[]) => void): void {
        this.onChange = fn;
    }

    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public remove(index: number): void {
        if (!this.disabled) {
            const newValue = this.values.slice();
            newValue.splice(index, 1);
            this.writeValue(newValue);
            this.onBlur();
        }
    }

    public inputChanged(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.onChangeInput.emit(value);
    }

    public onDelete(): void {
        if (this.values.length) {
            this.remove(this.values.length - 1);
        }
    }

    public onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    public focus(): void {
        this.input.nativeElement.focus();
    }

    private onChange: (values: string[]) => void = () => {
    }
    private onTouched: () => void = () => {
    }

    private getItem(value: string): IAutocompleteItem {
        if (!this.autocompleteList) {
            return {value, label: value};
        }

        return this.autocompleteList.find((item: IAutocompleteItem) => item.value === value) || {value, label: value};
    }

    private isDisabledValue(value: string): boolean {
        if (this.disabled || value === "") {
            return false;
        }
        if (this.disableFreeText) {
            return Boolean(this.autocompleteList.find((autcompleteItem: IAutocompleteItem) => autcompleteItem.value === value));
        }
        return true;
    }

    private add(value: string): void {
        if (this.isDisabledValue(value)) {
            const newValue: string = this.transform(value);
            this.writeValue(
                this.values.concat([newValue])
            );
            this.onAdd.emit(this.values);
            this.input.nativeElement.value = "";
        }
    }

    private transform(value: string): string {
        if (this.toUpper) {
            return value.toUpperCase();
        }

        return value;
    }

    private generateItems(): void {
        // We need to preserve old values codes, even if we don't have them
        // in the autocomplete list (because the user typed something else)
        // And we also need to be able to replace a code by its value
        // even if the autocomplete list comes after setting the value
        const oldItemsByValue: { [value: string]: IAutocompleteItem } =
            this.items.reduce((byValue, item: IAutocompleteItem) => ({...byValue, [item.value]: item}), {});

        this.items = (this.values || []).map((value: string, index: number) => {
            if (oldItemsByValue[value] && oldItemsByValue[value].value !== oldItemsByValue[value].label) {
                return oldItemsByValue[value];
            }

            return this.getItem(value);
        });
    }

}
